/**
 * CoachFeedbackPane (MED-330)
 * Side pane for coach midpoint/endpoint feedback.
 * Shows editable form if not submitted, read-only view if already submitted.
 * Matches existing coach session feedback side pane style.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Text,
  Stack,
  Group,
  Button,
  Textarea,
  Rating,
  Divider,
  Alert,
  Loader,
  Paper,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CheckCircle, Info } from 'react-feather';
import coachPortalApi, { type CoachFeedbackData } from '../../../services/api/coachPortal';
import type { FeedbackSurveyQuestion } from '../../../services/api/feedbackSurveys';
import { COACH_FEEDBACK_INTRO } from './coachFeedbackConstants';

interface CoachFeedbackPaneProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  cohortName: string;
  surveyType: 'midpoint' | 'endpoint';
  onSuccess?: () => void;
}

export function CoachFeedbackPane({
  opened,
  onClose,
  cohortId,
  cohortName,
  surveyType,
  onSuccess,
}: CoachFeedbackPaneProps) {
  const [data, setData] = useState<CoachFeedbackData | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState<Record<string, unknown>>({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await coachPortalApi.getCoachFeedback(cohortId, surveyType);
      setData(result);
      if (result.submission) {
        setResponses(result.submission.parsed_data?.responses || {});
        setSubmitted(true);
      } else {
        setResponses({});
        setSubmitted(false);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cohortId, surveyType]);

  useEffect(() => {
    if (opened) {
      loadData();
    }
  }, [opened, loadData]);

  const handleSubmit = async () => {
    if (!data?.config) return;
    setSubmitting(true);
    try {
      await coachPortalApi.submitCoachFeedback(cohortId, surveyType, responses);
      setSubmitted(true);
      onSuccess?.();
      onClose();
      notifications.show({
        title: 'Feedback Submitted',
        message: `Your ${surveyType} feedback has been submitted. Thank you!`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Submission Failed',
        message: 'Unable to submit feedback. Please try again.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResponseChange = (questionId: string, value: unknown) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const surveyLabel = surveyType === 'midpoint' ? 'Midpoint' : 'Endpoint';
  const introText = COACH_FEEDBACK_INTRO[surveyType];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Text fw={600} size="lg">
            {surveyLabel} Feedback
          </Text>
          <Text size="sm" c="dimmed">
            {cohortName}
          </Text>
        </div>
      }
      position="right"
      size="lg"
      padding="lg"
      classNames={{ content: 'med-mobile-drawer' }}
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : !data?.config ? (
        <Alert icon={<Info size={16} />} color="gray">
          No feedback survey is available for this cohort yet.
        </Alert>
      ) : submitted && !submitting ? (
        <Stack gap="lg">
          {/* Confirmation state */}
          <Alert icon={<CheckCircle size={16} />} color="green" variant="light">
            Thank you! Your {surveyLabel.toLowerCase()} feedback has been submitted.
          </Alert>

          {/* Read-only view of submitted responses */}
          <ReadOnlyResponses questions={data.questions} responses={responses} />
        </Stack>
      ) : (
        <Stack gap="lg">
          {/* Intro paragraph */}
          <Paper p="md" radius="sm" bg="gray.0">
            <Text size="sm" c="dimmed">
              {introText}
            </Text>
          </Paper>

          {/* Questions */}
          {data.questions.map((question) => (
            <QuestionField
              key={question.id}
              question={question}
              value={responses[question.id]}
              onChange={(value) => handleResponseChange(question.id, value)}
              readOnly={false}
            />
          ))}

          {/* Actions */}
          <Group justify="flex-end" pt="md" className="med-mobile-footer">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!hasRequiredResponses(data.questions, responses)}
            >
              Submit Feedback
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

function QuestionField({
  question,
  value,
  onChange,
  readOnly,
}: {
  question: FeedbackSurveyQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
}) {
  return (
    <Stack gap={4}>
      <Divider />
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} pt="xs">
        {question.question_text}
      </Text>
      {question.question_type === 'scale' ? (
        <Rating
          value={Number(value) || 0}
          onChange={readOnly ? undefined : (v) => onChange(v)}
          readOnly={readOnly}
          count={question.scale_max || 5}
          size="lg"
        />
      ) : (
        <Textarea
          value={String(value || '')}
          onChange={readOnly ? undefined : (e) => onChange(e.currentTarget.value)}
          readOnly={readOnly}
          autosize
          minRows={3}
          maxRows={8}
          placeholder={readOnly ? '' : 'Share your thoughts...'}
          styles={readOnly ? { input: { backgroundColor: 'var(--mantine-color-gray-0)' } } : {}}
        />
      )}
    </Stack>
  );
}

function ReadOnlyResponses({
  questions,
  responses,
}: {
  questions: FeedbackSurveyQuestion[];
  responses: Record<string, unknown>;
}) {
  return (
    <Stack gap="md">
      {questions.map((question) => {
        const answer = responses[question.id];
        if (answer === undefined || answer === null || answer === '') return null;
        return (
          <QuestionField
            key={question.id}
            question={question}
            value={answer}
            onChange={() => {}}
            readOnly
          />
        );
      })}
    </Stack>
  );
}

function hasRequiredResponses(
  questions: FeedbackSurveyQuestion[],
  responses: Record<string, unknown>,
): boolean {
  return questions
    .filter((q) => q.is_required)
    .every((q) => {
      const val = responses[q.id];
      return val !== undefined && val !== null && val !== '';
    });
}

export default CoachFeedbackPane;
